<template>
  <div class="d-flex align-items-center">
    <div class="d-flex time-component align-items-center">
      <button
        v-if="showIncrementButtons"
        class="btn btn-sm increment-btn"
        :disabled="isMinIncrement"
        type="button"
        @click="changeIncrement(-1)"
      >
        <i class="fas fa-minus" />
      </button>

      <RsDropDownScroll
        :selected="selectedTimeslotText"
        :pick-options="hourlyOptions"
        :is-plain-options="false"
        class="mx-1 w-100"
        @onselect="onSelectedTimeslot"
      />

      <button
        v-if="showIncrementButtons"
        class="btn btn-sm increment-btn"
        :disabled="isMaxIncrement"
        type="button"
        @click="changeIncrement(1)"
      >
        <i class="fas fa-plus" />
      </button>
    </div>

    <div class="clock">
      <i class="far fa-clock" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ISelectOption } from '@/shared';
import { getTimeslotsOfDay, THourFormat } from '@/shared/helpers/get-timeslots-of-day';

dayjs.extend(customParseFormat);

const INCREMENTS = [5, 10, 15, 30, 60];

export default defineComponent({
  name: 'RsTimeSlotSelector',
  props: {
    /**
     * Interval in between each time slot
     */
    increment: {
      type: Number,
      default: 30,
    },
    /**
     * Indicate whether to display the 12-hr or 24-hr format for the text of each time slot
     */
    textOptionFormat: {
      type: String as PropType<THourFormat>,
      default: '12',
      validator: (v: string) => ['12', '24'].includes(v),
    },
    /**
     * Determins whether to use the 12-hr or 24-hr format for the value of each time slot
     */
    valueOptionFormat: {
      type: String as PropType<THourFormat>,
      default: '24',
      validator: (v: string) => ['12', '24'].includes(v),
    },
    /**
     * Selected value
     */
    selectedTimeslot: {
      type: String,
      default: '',
    },
    /**
     * Determines whether to show the -/+ buttons for changing the increment
     */
    showIncrementButtons: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hourlyOptions: [] as ISelectOption[],
      selectedTimeslotText: '',
      localIncrement: this.increment,
    };
  },
  computed: {
    incrementIndex(): number {
      return INCREMENTS.indexOf(this.localIncrement);
    },
    isMinIncrement(): boolean {
      return this.incrementIndex <= 0;
    },
    isMaxIncrement(): boolean {
      return this.incrementIndex >= INCREMENTS.length - 1;
    },
  },
  watch: {
    selectedTimeslot(newVal) {
      this.updateSelectedTimeslotText(newVal);
    },
    localIncrement() {
      this.refreshTimeslots();
      this.$emit('increment-change', this.localIncrement);
    },
  },
  mounted() {
    this.refreshTimeslots();
    this.updateSelectedTimeslotText(this.selectedTimeslot);
  },
  methods: {
    refreshTimeslots(): void {
      this.hourlyOptions = getTimeslotsOfDay(
        this.textOptionFormat,
        this.valueOptionFormat,
        this.localIncrement,
      );
      this.updateSelectedTimeslotText(this.selectedTimeslot);
    },
    changeIncrement(direction: 1 | -1): void {
      const nextIndex = this.incrementIndex + direction;
      if (nextIndex < 0 || nextIndex >= INCREMENTS.length) return;
      this.localIncrement = INCREMENTS[nextIndex];
    },
    normalize(time: string): string {
      return dayjs(time.trim(), ['H:mm', 'HH:mm'], true).format('HH:mm');
    },
    updateSelectedTimeslotText(timeslot: string): void {
      if (!timeslot) {
        this.selectedTimeslotText = '';
        return;
      }
      const option = this.hourlyOptions.find(
        (op) => this.normalize(op.value) === this.normalize(timeslot),
      );
      this.selectedTimeslotText = option?.text || '';
    },
    onSelectedTimeslot(data: string): void {
      this.$emit('timeslot-select', data);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.time-component {
  display: flex;
  border: 1px solid $tertiary-gray;
  border-radius: 5px 0 0 5px;
  height: 41px;
  align-items: center;

  .increment-btn {
    border: 1px solid $tertiary-gray;
    background-color: white;
    color: $secondary-color;
    width: 35px;
    height: 41px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    cursor: pointer;

    &:first-child {
      border-radius: 5px 0 0 5px;
      border-right: none;
    }

    &:last-child {
      border-radius: 0 0 0 0;
      border-left: none;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .rs-dropdown-scroll {
    flex: 1;
    height: 100%;
  }
}

.clock {
  height: 41px;
  width: 39px;
  text-align: center;
  margin-left: -0.03rem;
  border-radius: 0 4px 4px 0;
  background-color: $secondary-color;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
